import { __awaiter } from "tslib";
import { makeAutoObservable, runInAction, when } from "mobx";
import { applySnapshot, unprotect } from "@concord-consortium/mobx-state-tree";
import { typeConverter } from "../../utilities/db-utils";
import { MetadataDocMapModel } from "../document/document-metadata-model";
import { isSortableType } from "../document/document-types";
import { DocumentGroup } from "./document-group";
export class SortedDocuments {
    constructor(stores) {
        Object.defineProperty(this, "stores", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "metadataDocsFiltered", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: MetadataDocMapModel.create()
        });
        // `metadataDocsWithoutUnit` picks up unit-less docs (e.g. personal documents) when a unit
        // filter is applied.
        Object.defineProperty(this, "metadataDocsWithoutUnit", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: MetadataDocMapModel.create()
        });
        // `metadataDocsWholeUnit` picks up documents about a whole unit rather than any one investigation
        // or problem within it (class-wide collaborative documents) when an investigation or problem filter
        // is applied, which would otherwise exclude them.
        Object.defineProperty(this, "metadataDocsWholeUnit", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: MetadataDocMapModel.create()
        });
        Object.defineProperty(this, "docsReceived", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        // Maps from document ID to the history entry ID that the user requested to view.
        Object.defineProperty(this, "documentHistoryViewRequests", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: {}
        });
        // Root document group that serves as the root node for delegating all sorting operations.
        Object.defineProperty(this, "rootDocumentGroup", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        makeAutoObservable(this);
        this.stores = stores;
        // We only want MobX observability + MST serialization, not MST actions, on these maps.
        unprotect(this.metadataDocsFiltered);
        unprotect(this.metadataDocsWithoutUnit);
        unprotect(this.metadataDocsWholeUnit);
        this.rootDocumentGroup = new DocumentGroup({
            stores,
            sortType: "All",
            label: "All Documents",
            documents: () => this.filteredDocsByType
        });
    }
    get bookmarksStore() {
        return this.stores.bookmarks;
    }
    get class() {
        return this.stores.class;
    }
    get commentTags() {
        return this.stores.commentTags.mergedWith(this.stores.appConfig.commentTags);
    }
    get db() {
        return this.stores.db;
    }
    get documents() {
        return this.stores.documents;
    }
    get filteredDocsByType() {
        return this.firestoreMetadataDocs.filter((doc) => {
            return isSortableType(doc.type);
        });
    }
    get groupsStore() {
        return this.stores.groups;
    }
    get user() {
        return this.stores.user;
    }
    get curriculumConfig() {
        return this.stores.curriculumConfig;
    }
    setDocumentHistoryViewRequest(docKey, historyId) {
        this.documentHistoryViewRequests[docKey] = historyId;
    }
    getDocumentHistoryViewRequest(docKey) {
        // We only want to move to this history entry once,
        // so we delete the request after it has been fulfilled.
        const historyId = this.documentHistoryViewRequests[docKey];
        if (historyId) {
            delete this.documentHistoryViewRequests[docKey];
        }
        return historyId;
    }
    sortBy(sortType) {
        return this.rootDocumentGroup.sortBy(sortType);
    }
    watchFirestoreMetaDataDocs(filter, unit, investigation, problem) {
        const db = this.db.firestore;
        const converter = typeConverter();
        const baseQuery = db.collection("documents")
            .withConverter(converter)
            .where("context_id", "==", this.user.classHash);
        let filteredQuery = baseQuery;
        if (filter !== "All") {
            // an "in" query is used here so that we can find any documents that use unit and
            // any the older renamed unit codes.
            filteredQuery = filteredQuery.where("unit", "in", this.curriculumConfig.getUnitCodeVariants(unit));
        }
        if (filter === "Investigation" || filter === "Problem") {
            filteredQuery = filteredQuery.where("investigation", "==", String(investigation));
        }
        if (filter === "Problem") {
            filteredQuery = filteredQuery.where("problem", "==", String(problem));
        }
        const disposeFilteredListener = filteredQuery.onSnapshot(snapshot => {
            const mstSnapshot = this.stores.documentMetadata.getMSTSnapshotFromFBSnapshot(snapshot);
            runInAction(() => {
                applySnapshot(this.metadataDocsFiltered, mstSnapshot);
                this.docsReceived = true;
            });
        });
        let disposeDocsWithoutUnitListener;
        if (filter !== "All") {
            // We need to look for the unit-less documents like personal documents
            const queryForUnitNull = baseQuery.where("unit", "==", null);
            disposeDocsWithoutUnitListener = queryForUnitNull.onSnapshot(snapshot => {
                const mstSnapshot = this.stores.documentMetadata.getMSTSnapshotFromFBSnapshot(snapshot);
                applySnapshot(this.metadataDocsWithoutUnit, mstSnapshot);
            });
        }
        else {
            // If the filter is "All" then the metaDocsFiltered will include everything.
            this.metadataDocsWithoutUnit.clear();
        }
        let disposeWholeUnitListener;
        if (filter === "Investigation" || filter === "Problem") {
            // We only want to filter out documents with a **different** investigation or problem.
            // If the document is at the unit level (no investigation), we want to still show it.
            // This is the curriculum axis of the document, not the container axis. Documents
            // in the unit container that are about an investigation or problem are not included.
            const queryForWholeUnit = baseQuery
                .where("unit", "in", this.curriculumConfig.getUnitCodeVariants(unit))
                .where("investigation", "==", null);
            disposeWholeUnitListener = queryForWholeUnit.onSnapshot(snapshot => {
                const mstSnapshot = this.stores.documentMetadata.getMSTSnapshotFromFBSnapshot(snapshot);
                applySnapshot(this.metadataDocsWholeUnit, mstSnapshot);
            });
        }
        else {
            // The All and Unit filters already include these documents.
            this.metadataDocsWholeUnit.clear();
        }
        // A disposing function that calls the disposers from the
        // onSnapshot listeners.
        return () => {
            disposeFilteredListener();
            disposeDocsWithoutUnitListener === null || disposeDocsWithoutUnitListener === void 0 ? void 0 : disposeDocsWithoutUnitListener();
            disposeWholeUnitListener === null || disposeWholeUnitListener === void 0 ? void 0 : disposeWholeUnitListener();
        };
    }
    get exemplarMetadataDocs() {
        return this.stores.documentMetadata.exemplarMetadataDocs;
    }
    // What happens if the visibility changes on a metadata document?
    // - FS onSnapshot listener is called
    // - listener applies the snapshot to the map of objects
    // - the keys of the objects don't change in the map
    // - MobX should not re-run this view because it is only reading the key
    //   of each document.
    get firestoreMetadataDocs() {
        const matchedDocKeys = new Set();
        const docsArray = [];
        this.metadataDocsFiltered.forEach(doc => {
            docsArray.push(doc);
            matchedDocKeys.add(doc.key);
        });
        this.metadataDocsWithoutUnit.forEach(doc => {
            // If there is a duplicate for some reason just ignore the unit-less one
            if (matchedDocKeys.has(doc.key))
                return;
            docsArray.push(doc);
            matchedDocKeys.add(doc.key);
        });
        this.metadataDocsWholeUnit.forEach(doc => {
            // Also present in the filtered map under the All and Unit filters; dedupe by key.
            if (matchedDocKeys.has(doc.key))
                return;
            docsArray.push(doc);
            matchedDocKeys.add(doc.key);
        });
        this.exemplarMetadataDocs.forEach(doc => {
            // If there is a duplicate, it will have been merged with one of the previous
            // maps by the firestore snapshot listeners. So we ignore the duplicate here.
            if (matchedDocKeys.has(doc.key))
                return;
            docsArray.push(doc);
            matchedDocKeys.add(doc.key);
        });
        return docsArray;
    }
    // This only fetches documents that have metadata that was already pulled down
    // by the watchFirestoreMetaDataDocs listener.
    fetchFullDocument(docKey) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.docsReceived) {
                // Wait until the initial batch of documents has been received from Firestore.
                yield when(() => this.docsReceived);
            }
            const metadataDoc = this.firestoreMetadataDocs.find(doc => doc.key === docKey);
            if (!metadataDoc) {
                console.warn("Could not find metadata doc with key", docKey, this.firestoreMetadataDocs);
                return;
            }
            return this.db.openDocumentFromFirestoreMetadata(Object.assign(Object.assign({}, metadataDoc), { properties: metadataDoc.propertiesAsStringRecord }));
        });
    }
}
//# sourceMappingURL=data:application/json;base64,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